
import React, { useState } from 'react';
import { SubscriptionPlan, UserSubscription } from '../types';
import { CheckCircle2, X, CreditCard, Lock, ShieldCheck, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';

interface SubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPlan: SubscriptionPlan;
  onSubscribe: (plan: SubscriptionPlan, billing: 'monthly' | 'annual', amount: number) => Promise<void>;
}

const PLANS = [
  {
    id: 'Starter',
    name: 'Starter',
    priceMonthly: 0,
    priceAnnual: 0,
    features: ['Track 1 Property', 'Basic Analytics', 'Tax Calculator'],
    limit: '1 Unit'
  },
  {
    id: 'Investor',
    name: 'Investor',
    priceMonthly: 15,
    priceAnnual: 144, // 12/mo
    features: ['Track 10 Properties', 'AI Advisor Access', 'Document Storage', 'Deal Analyzer'],
    limit: '10 Units',
    popular: true
  },
  {
    id: 'Pro',
    name: 'Pro',
    priceMonthly: 49,
    priceAnnual: 468, // 39/mo
    features: ['Unlimited Units', 'Advanced Reporting', 'Priority Support', 'White-label Exports'],
    limit: 'Unlimited'
  }
];

const SubscriptionModal: React.FC<SubscriptionModalProps> = ({ isOpen, onClose, currentPlan, onSubscribe }) => {
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan>(currentPlan === 'Starter' ? 'Investor' : 'Pro');
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('annual');
  const [step, setStep] = useState<'plan' | 'payment'>('plan');
  const [isProcessing, setIsProcessing] = useState(false);

  // Payment Form State
  const [cardName, setCardName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvc, setCvc] = useState('');

  if (!isOpen) return null;

  const handleContinue = () => {
      if (selectedPlan === 'Starter') {
          onClose();
          return;
      }
      setStep('payment');
  };

  const selectedPlanDetails = PLANS.find(p => p.id === selectedPlan)!;
  const price = billingPeriod === 'monthly' ? selectedPlanDetails.priceMonthly : selectedPlanDetails.priceAnnual;

  const handlePayment = async (e: React.FormEvent) => {
      e.preventDefault();
      setIsProcessing(true);
      
      // Simulate Stripe processing
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      await onSubscribe(selectedPlan, billingPeriod, price);
      setIsProcessing(false);
      onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm" onClick={onClose}></div>
      
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white w-full max-w-4xl rounded-3xl shadow-2xl overflow-hidden flex flex-col md:flex-row relative z-10"
      >
        {/* Close Button */}
        <button onClick={onClose} className="absolute top-4 right-4 p-2 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors z-20">
            <X className="w-5 h-5 text-slate-500" />
        </button>

        {/* Left Side: Plan Selection */}
        <div className={`flex-1 p-8 bg-slate-50 ${step === 'payment' ? 'hidden md:block md:w-1/3 md:border-r border-gray-200' : 'w-full'}`}>
            <div className="mb-6">
                <h2 className="text-2xl font-black text-slate-800 mb-2">Upgrade Plan</h2>
                <p className="text-slate-500 text-sm">Unlock the full potential of DC PropChain.</p>
            </div>

            {/* Billing Toggle */}
            <div className="flex bg-white p-1 rounded-xl border border-gray-200 mb-6 w-fit">
                <button 
                    onClick={() => setBillingPeriod('monthly')}
                    className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${billingPeriod === 'monthly' ? 'bg-slate-800 text-white shadow-md' : 'text-slate-500 hover:text-slate-800'}`}
                >
                    Monthly
                </button>
                <button 
                    onClick={() => setBillingPeriod('annual')}
                    className={`px-4 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-1 ${billingPeriod === 'annual' ? 'bg-slate-800 text-white shadow-md' : 'text-slate-500 hover:text-slate-800'}`}
                >
                    Annual <span className="text-[9px] bg-emerald-100 text-emerald-700 px-1.5 rounded-full">-20%</span>
                </button>
            </div>

            <div className="space-y-3">
                {PLANS.map(plan => (
                    <div 
                        key={plan.id}
                        onClick={() => setSelectedPlan(plan.id as SubscriptionPlan)}
                        className={`p-4 rounded-xl border-2 cursor-pointer transition-all relative ${
                            selectedPlan === plan.id 
                            ? 'border-emerald-500 bg-white shadow-lg transform scale-[1.02]' 
                            : 'border-transparent bg-white hover:border-gray-200'
                        }`}
                    >
                        {plan.popular && (
                            <div className="absolute -top-3 right-4 bg-emerald-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                                MOST POPULAR
                            </div>
                        )}
                        <div className="flex justify-between items-center mb-2">
                            <span className={`font-bold ${selectedPlan === plan.id ? 'text-emerald-600' : 'text-slate-700'}`}>{plan.name}</span>
                            <div className="text-right">
                                <span className="text-lg font-black text-slate-800">
                                    €{billingPeriod === 'monthly' ? plan.priceMonthly : (plan.priceAnnual / 12).toFixed(0)}
                                </span>
                                <span className="text-xs text-slate-400">/mo</span>
                            </div>
                        </div>
                        <ul className="space-y-1.5">
                            {plan.features.slice(0, 2).map((f, i) => (
                                <li key={i} className="flex items-center gap-2 text-xs text-slate-500">
                                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> {f}
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>

            {step === 'plan' && (
                 <div className="mt-8 flex justify-end">
                    <button 
                        onClick={handleContinue}
                        className="bg-emerald-600 hover:bg-emerald-500 text-white px-8 py-3 rounded-xl font-bold shadow-lg shadow-emerald-500/20 transition-all flex items-center gap-2 w-full justify-center"
                    >
                        Continue to Payment
                    </button>
                 </div>
            )}
        </div>

        {/* Right Side: Payment Form */}
        {step === 'payment' && (
            <div className="flex-[1.5] p-8 bg-white">
                 <div className="flex items-center gap-2 mb-6 text-slate-400 cursor-pointer hover:text-slate-600 md:hidden" onClick={() => setStep('plan')}>
                     ← Back to Plans
                 </div>

                 <div className="mb-8">
                     <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                         <Lock className="w-5 h-5 text-emerald-500" /> Secure Checkout
                     </h3>
                     <p className="text-sm text-slate-500 mt-1">Powered by Stripe (Simulated)</p>
                 </div>

                 {/* Order Summary */}
                 <div className="bg-slate-50 p-4 rounded-xl border border-gray-100 mb-8">
                     <div className="flex justify-between items-center mb-2">
                         <span className="font-bold text-slate-700">{selectedPlanDetails.name} Plan ({billingPeriod})</span>
                         <span className="font-bold text-slate-900">€{price}</span>
                     </div>
                     <div className="flex justify-between items-center text-xs text-slate-500 border-t border-gray-200 pt-2 mt-2">
                         <span>Total due today</span>
                         <span className="font-black text-emerald-600 text-lg">€{price}</span>
                     </div>
                 </div>

                 <form onSubmit={handlePayment} className="space-y-4">
                     <div>
                         <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Name on Card</label>
                         <input 
                            required
                            value={cardName}
                            onChange={e => setCardName(e.target.value)}
                            placeholder="John Doe"
                            className="w-full bg-white border border-gray-300 rounded-lg px-4 py-3 text-slate-800 outline-none focus:ring-2 focus:ring-emerald-500 transition-all"
                         />
                     </div>
                     
                     <div>
                         <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Card Information</label>
                         <div className="border border-gray-300 rounded-lg overflow-hidden bg-white focus-within:ring-2 focus-within:ring-emerald-500 transition-all">
                            <div className="flex items-center px-4 py-3 border-b border-gray-200">
                                <CreditCard className="w-5 h-5 text-slate-400 mr-3" />
                                <input 
                                    required
                                    value={cardNumber}
                                    onChange={e => setCardNumber(e.target.value)}
                                    placeholder="0000 0000 0000 0000"
                                    className="w-full outline-none text-slate-800"
                                />
                            </div>
                            <div className="flex divide-x divide-gray-200">
                                <input 
                                    required
                                    value={expiry}
                                    onChange={e => setExpiry(e.target.value)}
                                    placeholder="MM / YY"
                                    className="w-1/2 px-4 py-3 outline-none text-slate-800 text-center"
                                />
                                <input 
                                    required
                                    value={cvc}
                                    onChange={e => setCvc(e.target.value)}
                                    placeholder="CVC"
                                    className="w-1/2 px-4 py-3 outline-none text-slate-800 text-center"
                                />
                            </div>
                         </div>
                     </div>

                     <button 
                        type="submit"
                        disabled={isProcessing}
                        className="w-full bg-slate-900 hover:bg-slate-800 text-white py-4 rounded-xl font-bold shadow-lg flex items-center justify-center gap-2 transition-all mt-4 disabled:opacity-70"
                     >
                         {isProcessing ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                            <>
                                <ShieldCheck className="w-5 h-5" />
                                Pay €{price}
                            </>
                         )}
                     </button>
                     <div className="text-center">
                         <span className="text-[10px] text-slate-400 flex items-center justify-center gap-1">
                             <Lock className="w-3 h-3" /> Payments are secure and encrypted.
                         </span>
                     </div>
                 </form>
            </div>
        )}
      </motion.div>
    </div>
  );
};

export default SubscriptionModal;
